import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, StatusBadge } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { BarcodeScanner } from "@/components/wawi/BarcodeScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDateTime } from "@/lib/format";
import { Plus, Search, ScanLine, Smartphone, History } from "lucide-react";

const STATUS_MAP = {
  erwartet: { label: "Erwartet", cls: "bg-muted text-muted-foreground border-border" },
  auf_lager: { label: "Auf Lager", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" },
  reserviert: { label: "Reserviert", cls: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20" },
  unterwegs: { label: "Unterwegs", cls: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/20" },
  in_pruefung: { label: "In Prüfung", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20" },
  gesperrt: { label: "Gesperrt", cls: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20" },
  in_reparatur: { label: "In Reparatur", cls: "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20" },
  reklamiert: { label: "Reklamiert", cls: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20" },
  verkauft: { label: "Verkauft", cls: "bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-500/20" },
  ausgebucht: { label: "Ausgebucht", cls: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-300 border-zinc-500/20" },
};
const STATUSES = Object.keys(STATUS_MAP);

export default function WawiDevices() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [scan, setScan] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["wawi-devices", q, status], queryFn: async () => (await api.get(`/wawi/devices?q=${encodeURIComponent(q)}&status=${status}`)).data });

  const columns = [
    { key: "id", header: "Gerät", render: (d) => (
      <div className="min-w-0"><div className="truncate font-medium">{d.article_name} {d.variant_name && `· ${d.variant_name}`}</div>
        <div className="truncate text-xs text-muted-foreground font-mono">IMEI {d.imei1 || "—"}{d.serial ? ` · SN ${d.serial}` : ""}</div></div>
    ) },
    { key: "status", header: "Status", render: (d) => <StatusBadge status={d.status} map={STATUS_MAP} /> },
  ];

  return (
    <div>
      <PageHeader title="Geräte & IMEI" description={`${data?.total ?? 0} individuell verwaltete Geräte`}
        actions={can(user?.role, "wawi:write") && (
          <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setAddOpen(true)} data-testid="new-device-button"><Plus className="h-4 w-4" />Gerät erfassen</Button>
        )} />
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="IMEI, Seriennummer, EAN…" className="pl-9" data-testid="device-search-input" />
        </div>
        <Button variant="outline" onClick={() => setScan(true)} data-testid="device-scan-button"><ScanLine className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Scannen</span></Button>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="device-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle Status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_MAP[s].label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <DataTable columns={columns} rows={data?.items} loading={isLoading} testIdPrefix="device-row" onRowClick={(d) => setDetail(d.id)} />

      <BarcodeScanner open={scan} onOpenChange={setScan} title="IMEI/Seriennummer scannen" onResult={(v) => { setQ(v); setScan(false); toast.info(`Gescannt: ${v}`); }} />
      <AddDeviceDialog open={addOpen} onOpenChange={setAddOpen} onDone={() => qc.invalidateQueries({ queryKey: ["wawi-devices"] })} />
      <DeviceDrawer did={detail} onClose={() => setDetail(null)} canWrite={can(user?.role, "wawi:write")} onChanged={() => qc.invalidateQueries({ queryKey: ["wawi-devices"] })} />
    </div>
  );
}

function AddDeviceDialog({ open, onOpenChange, onDone }) {
  const [f, setF] = useState({ article_id: "", imei1: "", imei2: "", serial: "", warehouse_id: "" });
  const [scan, setScan] = useState(false);
  const { data: arts } = useQuery({ queryKey: ["wawi-art-min"], queryFn: async () => (await api.get("/wawi/articles?limit=200")).data, enabled: open });
  const { data: whs } = useQuery({ queryKey: ["wawi-wh-min"], queryFn: async () => (await api.get("/wawi/warehouses")).data, enabled: open });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.article_id) return toast.error("Bitte Artikel wählen");
    try {
      await api.post("/wawi/devices", f);
      toast.success("Gerät erfasst"); onOpenChange(false); setF({ article_id: "", imei1: "", imei2: "", serial: "", warehouse_id: "" }); onDone();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Gerät erfassen (Wareneingang)</DialogTitle>
          <DialogDescription>IMEI/Seriennummer scannen oder eingeben. Doppelte Identifikatoren werden abgelehnt.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Artikel</Label>
            <Select value={f.article_id} onValueChange={(v) => set("article_id", v)}>
              <SelectTrigger data-testid="device-article-select"><SelectValue placeholder="Artikel wählen" /></SelectTrigger>
              <SelectContent>{(arts?.items || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>IMEI 1</Label>
            <div className="flex gap-2">
              <Input value={f.imei1} onChange={(e) => set("imei1", e.target.value)} inputMode="numeric" data-testid="device-imei1-input" />
              <Button type="button" variant="outline" size="icon" onClick={() => setScan(true)} data-testid="device-imei-scan"><ScanLine className="h-4 w-4" /></Button>
            </div></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>IMEI 2</Label><Input value={f.imei2} onChange={(e) => set("imei2", e.target.value)} inputMode="numeric" /></div>
            <div className="space-y-1.5"><Label>Seriennummer</Label><Input value={f.serial} onChange={(e) => set("serial", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Lager</Label>
            <Select value={f.warehouse_id} onValueChange={(v) => set("warehouse_id", v)}>
              <SelectTrigger data-testid="device-warehouse-select"><SelectValue placeholder="Lager wählen" /></SelectTrigger>
              <SelectContent>{(whs?.items || []).map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="device-save-button">Erfassen</Button>
          </DialogFooter>
        </form>
        <BarcodeScanner open={scan} onOpenChange={setScan} title="IMEI scannen" onResult={(v) => { set("imei1", v); setScan(false); }} />
      </DialogContent>
    </Dialog>
  );
}

function DeviceDrawer({ did, onClose, canWrite, onChanged }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["wawi-device", did], queryFn: async () => (await api.get(`/wawi/devices/${did}`)).data, enabled: !!did });
  const d = data?.device;

  const changeStatus = async (s) => {
    try { await api.post(`/wawi/devices/${did}/status`, { status: s }); toast.success("Status geändert"); qc.invalidateQueries({ queryKey: ["wawi-device", did] }); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Sheet open={!!did} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><Smartphone className="h-4 w-4" />Gerätedetails</SheetTitle></SheetHeader>
        {d && (
          <div className="mt-4 space-y-4">
            <div><div className="text-sm font-semibold">{d.article_name} {d.variant_name}</div>
              <div className="mt-1 font-mono text-xs text-muted-foreground">IMEI1 {d.imei1 || "—"}<br />IMEI2 {d.imei2 || "—"}<br />SN {d.serial || "—"} · EAN {d.ean || "—"}</div>
              <div className="mt-2"><StatusBadge status={d.status} map={STATUS_MAP} /></div></div>
            {canWrite && (
              <div className="space-y-1.5"><Label>Status ändern</Label>
                <Select value={d.status} onValueChange={changeStatus}>
                  <SelectTrigger data-testid="device-status-change"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_MAP[s].label}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><History className="h-4 w-4" />Gerätehistorie</div>
              <div className="space-y-2">
                {(data?.history || []).map((h) => (
                  <div key={h.id} className="rounded-md border border-border px-3 py-2 text-xs">
                    <div className="font-medium capitalize">{h.action.replace("_", " ")}</div>
                    <div className="text-muted-foreground">{h.user_name} · {fmtDateTime(h.at)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
